import React, { createContext, useContext, useState, ReactNode } from "react";
import { DiscoveryRequestTaskService } from "../services/DiscoveryRequestTaskService";
import { DiscoveryRequestTask } from "../models/DiscoveryRequestTask";
import { useAuth } from "./AuthContext";
import { Filter } from "../models/Filter";
import { useMatter } from "./MatterContext";
import { clean } from "../utils/utils";
import { File } from "../models/File";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface DiscoveryRequestTaskContextProps {
  filter: Filter | null;
  setFilter: (filter: Filter | null) => void;
  selectedDiscoveryRequestTask: DiscoveryRequestTask | null;
  files: File[];
  createDiscoveryRequestTask: (
    data: Omit<
      DiscoveryRequestTask,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "matterId"
      | "updatedById"
    >,
  ) => Promise<string>;
  updateDiscoveryRequestTask: (
    id: string,
    data: Partial<Omit<DiscoveryRequestTask, "_id" | "createdAt">>,
  ) => Promise<string>;
  deleteDiscoveryRequestTaskById: (id: string) => Promise<void>;
  selectDiscoveryRequestTaskById: (id: string) => Promise<void>;
  loading: Loadings;
  error: Errors;
}

const DiscoveryRequestTaskContext = createContext<
  DiscoveryRequestTaskContextProps | undefined
>(undefined);

export const useDiscoveryRequestTask = (): DiscoveryRequestTaskContextProps => {
  const context = useContext(DiscoveryRequestTaskContext);
  if (!context) {
    throw new Error(
      "useDiscoveryRequestTask must be used within a DiscoveryRequestTaskProvider",
    );
  }
  return context;
};

export const DiscoveryRequestTaskProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [selectedDiscoveryRequestTask, setSelectedDiscoveryRequestTask] =
    useState<DiscoveryRequestTask | null>(null);
  const [selectedDiscoveryRequestTaskId, setSelectedDiscoveryRequestTaskId] =
    useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [filter, setFilter] = useState<Filter | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: false,
    details: false,
  });

  useSocketEventByIds(
    "discoveryrequesttasks:changed",
    ["*"],
    async (updatedDocument) => {
      if (updatedDocument._id === selectedDiscoveryRequestTaskId) {
        const discoveryRequestTask =
          await DiscoveryRequestTaskService.getDiscoveryRequestTask(
            selectedDiscoveryRequestTaskId!,
          );
        setSelectedDiscoveryRequestTask(discoveryRequestTask);
      }
    },
  );

  useSocketEventByIds("files:changed", ["*"], async (updatedDocument: File) => {
    if (
      updatedDocument.discoveryRequestTaskId === selectedDiscoveryRequestTaskId
    ) {
      const updatedFiles =
        await DiscoveryRequestTaskService.getDiscoveryRequestFiles(
          selectedDiscoveryRequestTaskId!,
        );
      setFiles(updatedFiles);
    }
  });

  const selectDiscoveryRequestTaskById = async (id: string) => {
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const [task, taskFiles] = await Promise.all([
        DiscoveryRequestTaskService.getDiscoveryRequestTask(id),
        DiscoveryRequestTaskService.getDiscoveryRequestFiles(id),
      ]);
      setSelectedDiscoveryRequestTask(task);
      setSelectedDiscoveryRequestTaskId(id);
      setFiles(taskFiles);
      setErrors((prev) => ({ ...prev, details: null }));
    } catch (err: any) {
      setSelectedDiscoveryRequestTask(null);
      setFiles([]);
      setErrors((prev) => ({ ...prev, details: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createDiscoveryRequestTask = async (
    data: Omit<
      DiscoveryRequestTask,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "matterId"
      | "updatedById"
    >,
  ): Promise<string> => {
    if (!user) throw new Error("User not authenticated");
    return DiscoveryRequestTaskService.createDiscoveryRequestTask({
      ...data,
      matterId: currentMatterId!,
    });
  };

  const updateDiscoveryRequestTask = async (
    id: string,
    data: Partial<Omit<DiscoveryRequestTask, "_id" | "createdAt">>,
  ): Promise<string> => {
    await DiscoveryRequestTaskService.updateDiscoveryRequestTask(
      id,
      clean(data),
    );
    if (id === selectedDiscoveryRequestTaskId) {
      await selectDiscoveryRequestTaskById(id);
    }
    return id;
  };

  const deleteDiscoveryRequestTaskById = async (id: string): Promise<void> => {
    if (!currentMatterId) return;
    await DiscoveryRequestTaskService.deleteDiscoveryRequestTask(id);
    if (id === selectedDiscoveryRequestTaskId) {
      setSelectedDiscoveryRequestTask(null);
      setSelectedDiscoveryRequestTaskId(null);
      setFiles([]);
    }
  };

  return (
    <DiscoveryRequestTaskContext.Provider
      value={{
        filter,
        setFilter,
        selectedDiscoveryRequestTask,
        selectDiscoveryRequestTaskById,
        files,
        loading: loadings,
        error: errors,
        createDiscoveryRequestTask,
        updateDiscoveryRequestTask,
        deleteDiscoveryRequestTaskById,
      }}
    >
      {children}
    </DiscoveryRequestTaskContext.Provider>
  );
};
